<script setup>
import { props as formGroupProps, useFormGroup } from '@phoenix/composables/components/form/formGroup';
import { computed, nextTick, onMounted, provide, ref, watch, inject } from 'vue';

const props = defineProps({
  ...formGroupProps,
  floatingLabel: {
    type: Boolean,
    default: true,
  },
});

const {
  _shouldDisplayCharLeft,
  charactersLeft,
  displayedErrors,
  errorId,
  field,
  fieldUniqueId,
  helpId,
  isCheckboxList,
  isDate,
  isHidden,
  isPhone,
  isInvalid,
  isSelect,
  isSensitive,
  labelId,
} = useFormGroup(props);

const shouldDisplayFilledCheckmark = inject('shouldDisplayFilledCheckmark', false);

const requirementsValidation = ref({});
const fieldFocused = ref(false);

const isSwitch = computed(() => ['checkbox', 'radio'].includes(field.value.type));

const formGroupClasses = computed(() => [
  'field',
  {
    'field--floating': hasFloatingLabel.value,
    'field--error': isInvalid.value,
    'field--switch': isSwitch.value,
    hidden: isHidden.value,
    'phoenix-field--phone': isPhone.value,
    'field--disabled': field.value.disabled,
  },
]);

const labelClasses = computed(() => ['label']);

const hasFloatingLabel = computed(
  () => props.floatingLabel && !isSwitch.value && !isCheckboxList.value && !forceRadio.value,
);

const forceRadio = computed(() => isSelect.value && !field.value.forceSelect && field.value.options.length <= 4);

const charLeftClasses = computed(() => 'field-helper-text');

const onPasswordValidation = (localRequirementsValidation) => {
  requirementsValidation.value = localRequirementsValidation;
};

const onFieldFocused = () => {
  fieldFocused.value = true;
};

onMounted(() => {
  nextTick(() => {
    if (hasFloatingLabel.value && !isDate.value && !isPhone.value) {
      const fieldElement = document.getElementById(fieldUniqueId.value);
      if (fieldElement) {
        const sgField = new Toolkit.Field(fieldElement);

        // In some cases, we need to force update the floating label when the field is not updated
        // by a focus or input event
        const unwatchValue = watch(
          () => field.value.value,
          () => {
            sgField.forceUpdate();
            unwatchValue();
          },
        );
      }
    }
  });
});

provide('onFieldFocused', onFieldFocused);
provide('onPasswordValidation', onPasswordValidation);
</script>

<template>
  <div v-bind="$attrs" :key="`form-group-${fieldUniqueId}`" :class="formGroupClasses" :data-cs-mask="isSensitive">
    <label
      v-if="field.label && !isHidden && !isSwitch && !forceRadio && showLabel"
      :id="labelId"
      :for="fieldUniqueId"
      :class="labelClasses"
    >
      {{ field.label }}&nbsp;
      <abbr v-if="field.required" :title="$t('Form.required')" class="label__required" data-cy="form-input-required"
        >*</abbr
      >
    </label>

    <div
      v-if="shouldDisplayFilledCheckmark && !['checkbox', 'radio'].includes(field.type)"
      class="field__input-wrapper"
    >
      <slot />
    </div>
    <slot v-else />

    <!--
      We should remove the inline style once SG has fixed the issue:
      https://liip.slack.com/archives/CAG99UR3K/p1731078648013459
    -->
    <span
      v-if="field.help"
      :id="helpId"
      class="field-helper-text rich-text"
      style="grid-column: 1 / span 2"
      v-html="field.help"
    />
    <slot v-else name="help" />

    <span v-if="_shouldDisplayCharLeft" :class="charLeftClasses">
      {{ $t('Form.charactersLeft', { charactersLeft }) }}
    </span>

    <span v-if="isInvalid" class="field-helper-text field-helper-text--error">
      {{ displayedErrors }}
    </span>

    <span v-if="isInvalid" :id="errorId" class="sr-only" role="alert">
      {{ $t('Accessibility.validationErrorMessage', { field: field.label, message: displayedErrors }) }}
    </span>

    <template v-if="field.requirements && fieldFocused">
      <ul class="field__criteria" data-cy="field-requirements" aria-hidden="true">
        <li
          v-for="requirement in field.requirements"
          :key="requirement"
          :class="[
            'field__criterion',
            { 'field__criterion--valid': requirementsValidation[requirement] },
            { 'field__criterion--invalid': requirementsValidation[requirement] === false },
          ]"
          :data-cy="`field-requirements-${requirement}`"
        >
          {{ $t(`Validation.custom.password.${requirement}`) }}
        </li>
      </ul>
    </template>
  </div>
</template>
